import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Painel } from '../../../../components/Painel';
import { Icon } from '../../../../components/Icon';

import {
  AddButton,
  Container,
  Content,
  CreateFichaCard,
  FichaCard,
  FichaCardActions,
  FichaCardHead,
  FichaFocus,
  FichaFooter,
  FichaName,
  Header,
  PageSubtitle,
  PageTitle,
  RemoveFichaButton,
  SplitBadge,
  SplitList,
  SplitRow,
  SplitTitle,
  TitleAccent,
} from './styles';

import { colors } from '../../../../styles/tokens';
import { useFeature } from '../../../../hooks/features';
import { useToast } from '../../../../hooks/toast';
import { TrainingTemplatesService } from '../../../../services/TrainingTemplatesService';
import {
  ITrainingTemplate,
  ITrainingTemplateSplit,
} from '../../../../domains/TrainingTemplate';

const SPLIT_LETTERS = 'ABCDEFGH';

// Split não tem nome próprio (não faz sentido numa ficha MODELO,
// reaproveitada por vários alunos) -- o card mostra os grupos
// musculares dos exercícios escolhidos no lugar de um título.
function splitMuscleGroupsLabel(split: ITrainingTemplateSplit): string {
  if (!split.exercises.length) {
    return 'Nenhum exercício ainda';
  }

  const groups = new Set(
    split.exercises.map(splitExercise => {
      const { muscle_groups } = splitExercise.exercise;
      const main =
        muscle_groups.find(mg => mg.is_main) || muscle_groups[0];

      return main?.muscle_group?.name || main?.name || 'Outro';
    }),
  );

  return Array.from(groups).join(', ');
}

const TrainingTemplates: React.FC = () => {
  const trainingTemplatesService = new TrainingTemplatesService();

  const navigate = useNavigate();
  const { addToast } = useToast();
  const hasTrainingTemplatesFeature = useFeature('training_templates');

  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState<ITrainingTemplate[]>([]);

  const loadTemplates = useCallback(() => {
    setLoading(true);
    trainingTemplatesService
      .getAll()
      .then(response => setTemplates(response.data))
      .catch(() => {
        addToast({
          type: 'error',
          title: 'Erro na busca dos templates de treino.',
          description:
            'Caso o problema persista, entre em contato com o administrador do sistema',
        });
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addToast]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const handleRemove = useCallback(
    (event: React.MouseEvent, template: ITrainingTemplate) => {
      event.stopPropagation();

      // eslint-disable-next-line no-alert
      if (!window.confirm(`Excluir o template "${template.name}"?`)) {
        return;
      }

      trainingTemplatesService
        .remove(template.id)
        .then(() => {
          setTemplates(current =>
            current.filter(current_template => current_template.id !== template.id),
          );
          addToast({ type: 'success', title: 'Template removido' });
        })
        .catch(() => {
          addToast({
            type: 'error',
            title: 'Não foi possível remover o template.',
          });
        });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [addToast],
  );

  return (
    <Painel loading={loading}>
      <Container>
        <Header>
          <div>
            <PageTitle>
              Templates de <TitleAccent>treino</TitleAccent>
            </PageTitle>
            <PageSubtitle>
              Modelos reutilizáveis de treino. Monte uma vez, aplique em
              qualquer aluno e ajuste o que for necessário depois.
            </PageSubtitle>
          </div>

          {hasTrainingTemplatesFeature ? (
            <AddButton
              type="button"
              onClick={() => navigate('/trainings/templates/new')}
            >
              <Icon name="add" size={18} color={colors.onPrimary} />
              Novo template
            </AddButton>
          ) : (
            <AddButton
              type="button"
              disabled
              title="Templates de treino é um recurso sob solicitação. Entre em contato para ativar."
            >
              <Icon name="lock" size={18} />
              Novo template
            </AddButton>
          )}
        </Header>

        <Content>
          {templates.map(template => {
            const exCount = template.splits.reduce(
              (count, split) => count + split.exercises.length,
              0,
            );

            return (
              <FichaCard
                key={template.id}
                onClick={() => navigate(`/trainings/templates/${template.id}`)}
              >
                <FichaCardHead>
                  <div>
                    <FichaName>{template.name}</FichaName>
                    <FichaFocus>{template.focus}</FichaFocus>
                  </div>
                  <FichaCardActions>
                    <RemoveFichaButton
                      type="button"
                      onClick={event => handleRemove(event, template)}
                    >
                      <Icon name="delete" size={20} />
                    </RemoveFichaButton>
                    <Icon
                      name="chevron_right"
                      color={colors.onSurfaceVariant}
                    />
                  </FichaCardActions>
                </FichaCardHead>

                <SplitList>
                  {template.splits.map((split, index) => (
                    <SplitRow key={split.id}>
                      <SplitBadge>
                        {SPLIT_LETTERS[index] || index + 1}
                      </SplitBadge>
                      <SplitTitle>
                        {splitMuscleGroupsLabel(split)}
                      </SplitTitle>
                    </SplitRow>
                  ))}
                </SplitList>

                <FichaFooter>
                  <span>{template.splits.length} splits</span>
                  <span>·</span>
                  <span>{exCount} exercícios</span>
                </FichaFooter>
              </FichaCard>
            );
          })}

          {hasTrainingTemplatesFeature && (
            <CreateFichaCard
              type="button"
              onClick={() => navigate('/trainings/templates/new')}
            >
              <Icon name="add_box" size={32} color={colors.onSurfaceVariant} />
              <span className="label">Criar grupo de splits</span>
            </CreateFichaCard>
          )}
        </Content>
      </Container>
    </Painel>
  );
};

export { TrainingTemplates };
